import { Component, Inject, Input, OnChanges, OnDestroy } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { Observable, Subscription, map, of } from 'rxjs';

import { AgentAdhocSourceResponse } from '../../../api/agents/models/curation-agent.api';
import { CustomInsights } from '../../../Monitor/Shared/models/custom-insights.model';
import { CoverageAnalysisService } from '../../../Monitor/Shared/services/coverage-analysis.service';
import {
    CardChartComponentHighlightColorEnum,
    CardChartComponentLayoutEnum
} from '../../../Common/Charts/components/CardChart/card-chart.component';
import { ReportSeriesModel } from '../../../Common/Reports/models/report-series.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { UsersService } from '../../../Core/Users/services/users.service';
import { CurationAgentActionModel } from '../../models/curation-agent-action.model';
import { folderActionTypes, tagActionTypes } from '../../enums/curation-agent-action-type.enum';
import { CurationAgentModel } from '../../models/curation-agent.model';
import { CurationAgentsService, sourceNameKey } from '../../services/curation-agents.service';
import { MonitoringSeriesTypeEnum } from '../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { agentSectionRoute, AgentRouteSectionEnum } from '../../AutomationAgentsRouter/automation-agents-route.const';
import { defaultSourceTypeIcon, sourceTypeIcons, sourceTypeLabels } from '../../enums/source-icons.const';

/**
 * What one ad hoc run did, shown in the report panel's right hand panel: how much it looked at and
 * kept, who ran it, and the agent that did the work.
 */
@Component({
    selector: 'agpr-ad-hoc-curation-report',
    templateUrl: './ad-hoc-curation-report.component.html',
    styleUrls: ['./ad-hoc-curation-report.component.scss'],
    standalone: false
})
export class AdHocCurationReportComponent implements OnChanges, OnDestroy {
    readonly messageSeverity = messageSeverityEnum;
    readonly cardColors = CardChartComponentHighlightColorEnum;
    readonly cardLayouts = CardChartComponentLayoutEnum;

    /** One series per card. A run is a finished thing, so there is no previous period to compare */
    analyzedSeries: ReportSeriesModel[] = [];
    curatedSeries: ReportSeriesModel[] = [];
    curationRateSeries: ReportSeriesModel[] = [];

    @Input() agent: CurationAgentModel;
    @Input() run: AgentAdhocSourceResponse;

    createdBy$: Observable<string>;
    /** Dates are shown in the viewer's own timezone */
    readonly userTimeZone$: Observable<string> = this.curationAgentsService.userTimeZone$;

    /** The prompt the agent judges mentions against, once it has been resolved by id */
    prompt: CustomInsights;

    /** The coverage the run was submitted from, once the run says where that was */
    sourceName: string = null;

    private readonly subscriptions: Subscription[] = [];

    constructor(
        private usersService: UsersService,
        private coverageAnalysisService: CoverageAnalysisService,
        private curationAgentsService: CurationAgentsService,
        @Inject(APP_BASE_HREF) private appBaseHref: string
    ) {}

    ngOnChanges(): void {
        this.analyzedSeries = [this.series('AutomationAgents.agent.dashboard.analyzed', this.analyzed)];
        this.curatedSeries = [this.series('AutomationAgents.agent.dashboard.curated', this.curated)];
        this.curationRateSeries = [
            this.series('AutomationAgents.agent.dashboard.curationRate', this.curationRate)
        ];

        this.createdBy$ = this.run?.createdByUserId
            ? this.usersService.getUserNameByID(this.run.createdByUserId)
            : of(null);

        this.subscriptions.push(
            this.coverageAnalysisService.customInsights$.pipe(
                map(prompts => prompts.find(item => item.id === this.agent?.customPromptId))
            ).subscribe(prompt => this.prompt = prompt),

            // Named from the shared lookup, the same way the dashboard names a live source
            this.curationAgentsService.sourceNames$.subscribe(names => this.sourceName = this.run?.sourceRef
                ? names.get(sourceNameKey(this.run.sourceType, this.run.sourceRef)) ?? this.run.sourceRef
                : null)
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /** Everything the agent does to a mention it keeps, and then what it does to the rest */
    get actions(): CurationAgentActionModel[] {
        return [...(this.agent?.positiveActions ?? []), ...(this.agent?.negativeActions ?? [])];
    }

    get analyzed(): number {
        return this.run?.totalMentions ?? 0;
    }

    get curated(): number {
        return this.run?.curatedMentions ?? 0;
    }

    /** Share of what the run looked at that it kept, as the dashboard's card reports it */
    get curationRate(): number {
        return this.analyzed ? (this.curated / this.analyzed) * 100 : 0;
    }

    /** One figure, in the shape agpr-card-chart reads it */
    private series(label: string, count: number): ReportSeriesModel {
        const series = new ReportSeriesModel();
        series.label = label;
        series.dataProvider.next([{ count: count }]);

        return series;
    }

    /** Only a folder or tag has somewhere of its own to open, as on the agent card */
    canViewActionTarget(action: CurationAgentActionModel): boolean {
        return !!action.referenceIds.length
            && (folderActionTypes.includes(action.type) || tagActionTypes.includes(action.type));
    }

    /** Opens the folder or tag the action saves to - whichever of its targets was clicked - in a new tab */
    viewActionTarget(action: CurationAgentActionModel, referenceId: string): void {
        this.curationAgentsService.viewActionTarget(action, referenceId);
    }

    /** The agent's own dashboard, for the link on its name. The route is rooted, the base href is not */
    get agentUrl(): string {
        return this.agent?.id
            ? this.appBaseHref + agentSectionRoute(this.agent.id, AgentRouteSectionEnum.dashboard).slice(1)
            : null;
    }

    /** The type the source is of, named and drawn the same way the sidebar names one */
    get sourceTypeLabel(): string {
        return sourceTypeLabels[this.run?.sourceType as MonitoringSeriesTypeEnum] ?? 'AutomationAgents.notAvailable';
    }

    get sourceIcon(): string {
        return sourceTypeIcons[this.run?.sourceType as MonitoringSeriesTypeEnum] ?? defaultSourceTypeIcon;
    }
}
